"use client";
import { n as useAnalyticsContext, r as assignRef } from "./atoms-provider-g2ndkvacik1ctkmd.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
//#region src/helpers/useObservabilitySafe.ts
var noop = () => {};
/**
* The fail-safe wrapper every instrumented component calls — never the context
* directly. Swallows transport errors so an app that never initialized
* observability doesn't crash.
*
* Defensive on two axes:
* - **Resolution:** a host that returns a malformed value (null / missing
*   method) falls back to no-op via `?.`. We deliberately do NOT wrap the hook
*   call itself in try/catch — `useObservability` may call hooks, and swallowing
*   a throw mid-hook-sequence would corrupt React's hook state (worse than a
*   clean error). A host hook that throws synchronously is its own
*   rules-of-hooks violation.
* - **Dispatch:** the inner call is wrapped so a throwing transport is swallowed.
*
* Identity is stable by design (the returned wrapper is memoized with `[]`), so
* components can list it in their own `useCallback` deps without busting
* memoization every render — host `useObservability` hooks commonly return a
* fresh function each render. To avoid the stale-closure tradeoff that a plain
* captured value would have, the wrapper dispatches through a ref that tracks
* the latest transport: if the host swaps it at runtime (e.g. consent
* revocation), already-mounted components immediately use the new one.
*/
function useObservabilitySafe() {
	const { useObservability } = useAnalyticsContext();
	const hostTrack = useObservability()?.trackJourneyEvent ?? noop;
	const latestTrack = useRef(hostTrack);
	useEffect(() => {
		latestTrack.current = hostTrack;
	}, [hostTrack]);
	const trackJourneyEvent = useMemo(() => (categoryName, eventName, eventData, contextualData) => {
		try {
			latestTrack.current(categoryName, eventName, eventData, contextualData);
		} catch {}
	}, []);
	return useMemo(() => ({ trackJourneyEvent }), [trackJourneyEvent]);
}
//#endregion
//#region src/helpers/use-impression-ref.ts
/**
* Returns a ref callback that fires a one-time `*ViewEvent` when the attached
* element first enters the viewport. Merge-free for callers: the consumer's
* ref is composed in, so a component passes the result straight to its element.
*
* Two properties matter:
* - **Bundle isolation.** `react-intersection-observer` is loaded via a dynamic
*   `import()` INSIDE the effect, so it sits in an on-demand chunk. A component
*   that merely offers view tracking never pulls rio into its static bundle, and
*   a consumer who leaves `active` false never loads it.
* - **No remount.** The observer attaches to the element the component already
*   renders (via the returned ref) — the element is never wrapped in Suspense or
*   cloned into a new subtree, so uncontrolled value, focus, caret, and scroll
*   are never disturbed.
*
* The observed element is tracked as state (not a ref) because the effect that
* sets up the observer depends on it — `setNode` runs from the ref callback at
* attach time, and the effect re-runs when the node changes.
*/
function useImpressionRef(active, analyticsName, eventName, consumerRef, inViewThreshold = 0) {
	const { trackJourneyEvent } = useObservabilitySafe();
	const [node, setNode] = useState(null);
	useEffect(() => {
		if (!active || !analyticsName || !node) return void 0;
		let cancelled = false;
		let unobserve;
		(async () => {
			try {
				const { observe } = await import("./react-intersection-observer-y6p9r3ir9fbcq7kr.js");
				if (cancelled) return;
				unobserve = observe(node, (inView) => {
					if (inView) {
						trackJourneyEvent("platform", eventName, { targetName: analyticsName });
						unobserve?.();
					}
				}, { threshold: inViewThreshold });
			} catch (error) {
				if (process.env.NODE_ENV && process.env.NODE_ENV !== "production") console.warn("[Atoms] view tracking unavailable:", error);
			}
		})();
		return () => {
			cancelled = true;
			unobserve?.();
		};
	}, [
		active,
		analyticsName,
		node,
		eventName,
		inViewThreshold,
		trackJourneyEvent
	]);
	return useCallback((element) => {
		setNode(element);
		assignRef(consumerRef, element);
	}, [consumerRef]);
}
//#endregion
export { useObservabilitySafe as n, useImpressionRef as t };

